import React, { useState, useEffect } from 'react';
import { Button, Modal, Alert } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import RankingService from '../../../services/RankingService';
import { useAdminCollections } from '../../../context/AdminCollections';
import { devError } from '../../../utils/devLog';

const RankingSelector = ({
  nominationId,
  sectionalCommitteeId,
  candidateName,
  allCandidates,
  currentRanking,
  onRankingSuccess,
  committeeRankings,
  electionTypeId,
}) => {
  const [selectedRank, setSelectedRank] = useState(currentRanking || null);
  const [tempRank, setTempRank] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const { triggerNominationStateRefresh, nominationStateArray } =
    useAdminCollections();

  const getNominationStateId = () => {
    const nominationState = nominationStateArray.find(
      (ns) => ns.attributes?.nominationId === nominationId,
    );
    return nominationState?.id;
  };

  useEffect(() => {
    setSelectedRank(currentRanking || null);
  }, [currentRanking]);

  const currentCommitteeRankings =
    committeeRankings?.[sectionalCommitteeId] || [];

  const getNextAvailableRank = () => {
    const existingRanks = currentCommitteeRankings
      .filter((r) => r.attributes.nominationId !== nominationId)
      .map((r) => r.attributes.ranking);

    for (let rank = 1; rank <= 4; rank += 1) {
      if (!existingRanks.includes(rank)) {
        return rank;
      }
    }
    return null;
  };

  const handleRankSelect = (rank) => {
    setTempRank(rank);
    setShowRemoveConfirm(false);
  };

  const handleSubmit = async () => {
    const nominationStateId = getNominationStateId();

    if (tempRank && nominationStateId) {
      try {
        await RankingService.saveRanking({
          nominationStateId,
          ranking: tempRank,
          electionTypeId,
        });

        setSelectedRank(tempRank);

        if (triggerNominationStateRefresh) {
          await triggerNominationStateRefresh(nominationId);
        }

        if (onRankingSuccess) {
          onRankingSuccess(candidateName, tempRank);
        }
      } catch (error) {
        devError('Failed to save ranking:', error);
        alert('Failed to save ranking. Please try again.');
        return;
      }
    }
    setShowModal(false);
    setTempRank(null);
    setShowRemoveConfirm(false);
  };

  const handleRemoveClick = () => {
    setShowRemoveConfirm(true);
  };

  const handleConfirmRemove = async () => {
    const nominationStateId = getNominationStateId();

    setIsRemoving(true);
    try {
      await RankingService.removeRanking(nominationStateId);

      setSelectedRank(null);

      if (triggerNominationStateRefresh) {
        await triggerNominationStateRefresh(nominationId);
      }

      if (onRankingSuccess) {
        onRankingSuccess(candidateName, null);
      }

      setShowModal(false);
      setShowRemoveConfirm(false);
    } catch (error) {
      devError('Failed to remove ranking:', error);
      alert('Failed to remove ranking. Please try again.');
    } finally {
      setIsRemoving(false);
    }
  };

  const handleCancelRemove = () => {
    setShowRemoveConfirm(false);
  };

  const handleShowModal = () => {
    setTempRank(selectedRank || nextAvailableRank);
    setShowModal(true);
    setShowRemoveConfirm(false);
  };

  const handleButtonClick = () => {
    handleShowModal();
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setTempRank(null);
    setShowRemoveConfirm(false);
  };

  const getCandidateNameByRank = (rank) => {
    const ranking = currentCommitteeRankings.find(
      (r) =>
        r.attributes.ranking === rank &&
        r.attributes.nominationId !== nominationId,
    );

    if (!ranking) return null;

    const rankingCandidateId = ranking.attributes.candidateId;
    return (
      allCandidates?.[rankingCandidateId] || `Candidate ${rankingCandidateId}`
    );
  };

  const isRankAvailable = (rank) => {
    const nextAvailable = getNextAvailableRank();
    const isCurrentlyAssigned = selectedRank === rank;

    if (isCurrentlyAssigned) {
      return true;
    }

    return rank === nextAvailable;
  };

  const nextAvailableRank = getNextAvailableRank();
  const allRanksFilled = nextAvailableRank === null && !selectedRank;

  return (
    <div>
      <Button
        variant={allRanksFilled || selectedRank ? 'outline-success' : 'success'}
        className="standard-width-button"
        onClick={handleButtonClick}
      >
        {selectedRank && <i className="bi bi-check-circle me-1" />}
        {(() => {
          if (selectedRank) {
            return `Rank ${selectedRank}`;
          }
          if (allRanksFilled) {
            return 'All Ranks Filled';
          }
          return 'Select Rank';
        })()}
      </Button>

      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="d-flex align-items-center gap-2">
              <span>Select Ranking for {candidateName}</span>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4">
          <div>
            {showRemoveConfirm ? (
              <Alert variant="warning">
                <div className="d-flex flex-column gap-3">
                  <div>
                    <i className="fas fa-exclamation-triangle me-2" />
                    <strong>Confirm Ranking Removal</strong>
                    <p className="mt-2 mb-0">
                      Are you sure you want to remove Rank {selectedRank} from{' '}
                      {candidateName}? This action will free up the rank for
                      assignment to another candidate.
                    </p>
                  </div>
                  <div className="d-flex gap-2 justify-content-end">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleCancelRemove}
                      disabled={isRemoving}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={handleConfirmRemove}
                      disabled={isRemoving}
                    >
                      {isRemoving ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          />
                          Removing...
                        </>
                      ) : (
                        <div>
                          <i className="fas fa-trash-alt me-2" />
                          Remove Ranking
                        </div>
                      )}
                    </Button>
                  </div>
                </div>
              </Alert>
            ) : (
              <div>
                <div className="d-flex flex-column gap-2">
                  {[1, 2, 3, 4].map((rank) => {
                    const isAvailable = isRankAvailable(rank);
                    const currentHolder = getCandidateNameByRank(rank);
                    const isCurrentSelection = tempRank === rank;
                    const isCurrentlyAssigned = selectedRank === rank;

                    let buttonVariant;
                    if (isCurrentSelection) {
                      buttonVariant = 'primary';
                    } else if (isAvailable) {
                      buttonVariant = 'outline-primary';
                    } else {
                      buttonVariant = 'outline-secondary';
                    }

                    const canClick = isAvailable && !isCurrentlyAssigned;

                    const getBadgeClassName = () => {
                      if (isCurrentSelection) {
                        return 'bg-white text-primary';
                      }
                      if (isAvailable && !isCurrentlyAssigned) {
                        return 'bg-primary text-white';
                      }
                      if (isCurrentlyAssigned) {
                        return 'bg-white text-primary';
                      }
                      return 'bg-secondary text-white';
                    };

                    return (
                      <Button
                        key={rank}
                        variant={buttonVariant}
                        onClick={() => canClick && handleRankSelect(rank)}
                        disabled={!isAvailable}
                        className="d-flex align-items-center text-start p-3"
                        style={{
                          cursor: canClick ? 'pointer' : 'not-allowed',
                          transition: 'all 0.15s ease',
                        }}
                        onMouseEnter={(e) => {
                          if (canClick) {
                            e.currentTarget.style.transform =
                              'translateY(-1px)';
                            e.currentTarget.style.boxShadow =
                              '0 2px 8px rgba(0,0,0,0.1)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (canClick) {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          }
                        }}
                      >
                        <div className="d-flex align-items-center gap-3 flex-grow-1">
                          <div
                            className={`rounded-circle d-flex align-items-center justify-content-center ${getBadgeClassName()}`}
                            style={{
                              width: '45px',
                              height: '45px',
                              fontSize: '20px',
                              fontWeight: 'bold',
                              flexShrink: 0,
                            }}
                          >
                            {rank}
                          </div>

                          <div className="flex-grow-1">
                            {isCurrentSelection && isCurrentlyAssigned && (
                              <div className="text-success fs-4 fw-semibold">
                                Currently assigned to {candidateName}
                              </div>
                            )}

                            {isCurrentSelection && !isCurrentlyAssigned && (
                              <div className="text-success fs-4 fw-semibold">
                                Will assign to {candidateName}
                              </div>
                            )}

                            {!isCurrentSelection && currentHolder && (
                              <div className="text-black fs-4">
                                {currentHolder}
                              </div>
                            )}

                            {!isCurrentSelection &&
                              !currentHolder &&
                              !isAvailable && (
                                <div className="text-secondary fs-4">
                                  Not yet available
                                </div>
                              )}
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-2 ms-2">
                          {!isAvailable && !isCurrentSelection && (
                            <i className="fas fa-lock text-muted" />
                          )}

                          {isCurrentlyAssigned && (
                            <div>
                              <div
                                data-tooltip-id={`remove-ranking-${nominationId}`}
                                data-tooltip-content="Remove ranking from this candidate"
                              >
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="text-danger p-0"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveClick();
                                  }}
                                  aria-label="Remove ranking"
                                >
                                  <i className="fas fa-trash-alt fa-lg" />
                                </Button>
                              </div>
                              <Tooltip
                                id={`remove-ranking-${nominationId}`}
                                place="top"
                              />
                            </div>
                          )}
                        </div>
                      </Button>
                    );
                  })}
                </div>

                {!selectedRank && nextAvailableRank && (
                  <div className="mt-3 p-4 bg-light rounded border border-primary">
                    <div className="d-flex align-items-center">
                      <i className="fas fa-info-circle text-primary me-2" />
                      <div className="mb-0">
                        <strong>
                          Current available rank: {nextAvailableRank}
                        </strong>
                        <br />
                        Rankings must be assigned consecutively. Complete rank{' '}
                        {nextAvailableRank} before moving to the next rank.
                      </div>
                    </div>
                  </div>
                )}

                {selectedRank && (
                  <div className="mt-3 p-4 bg-light rounded">
                    <div>
                      <i className="fas fa-info-circle me-1" />
                      This candidate is currently assigned Rank {selectedRank}.
                      You can remove this ranking to reassign it to another
                      candidate.
                    </div>
                  </div>
                )}

                {allRanksFilled && (
                  <div className="mt-3 p-4 bg-light rounded">
                    <div>
                      <i className="fas fa-exclamation-circle me-1" />
                      All ranks (1-4) have been assigned. Remove a ranking from
                      another candidate to make it available.
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          {!showRemoveConfirm && (
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!tempRank}
            >
              Submit
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

RankingSelector.propTypes = {
  nominationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  sectionalCommitteeId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  candidateName: PropTypes.string,
  allCandidates: PropTypes.object,
  currentRanking: PropTypes.number,
  onRankingSuccess: PropTypes.func,
  committeeRankings: PropTypes.object,
  electionTypeId: PropTypes.number,
};

export default RankingSelector;
